import { Address, AddressDetail } from "@campaign/shared-types";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, ReactNode, useCallback, useContext } from "react";
import { useWebSocket } from "../hooks/useWebSocket";
import { getAddressDetail, getAddresses } from "../services/api";

// Query key for addresses - used for cache management
export const ADDRESSES_QUERY_KEY = ["addresses"] as const;

interface AddressContextType {
  addresses: Address[];
  loading: boolean;
  error: string | null;
  updateAddress: (addressId: string, updates: Partial<Address>) => void;
  removeAddress: (addressId: string) => void;
  refreshAddresses: () => Promise<void>;
  refreshAddress: (addressId: string) => Promise<void>;
  loadAddressDetail: (addressId: string) => Promise<AddressDetail>;
}

const AddressContext = createContext<AddressContextType | undefined>(undefined);

export function useAddresses() {
  const context = useContext(AddressContext);
  if (!context) {
    throw new Error("useAddresses must be used within an AddressProvider");
  }
  return context;
}

export function AddressProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  // Use TanStack Query for fetching and caching addresses
  const {
    data: addresses = [],
    isLoading,
    error: queryError,
    refetch,
  } = useQuery({
    queryKey: ADDRESSES_QUERY_KEY,
    queryFn: async () => {
      console.log("Loading all addresses from API...");
      const allAddresses = await getAddresses();
      console.log(`Loaded ${allAddresses.length} addresses from API`);
      return allAddresses;
    },
    // These are set globally in queryClient, but can override here if needed
    // staleTime: 24 * 60 * 60 * 1000, // 24 hours
    // gcTime: 7 * 24 * 60 * 60 * 1000, // 7 days
  });

  // Convert React Query error to string
  const error = queryError ? (queryError as Error).message : null;

  // Listen to WebSocket messages to update addresses in real-time
  useWebSocket({
    onMessage: (message) => {
      // Handle contact added - update address's lastContactedAt
      if (
        message.type === "contactAdded" &&
        message.addressId &&
        message.contact
      ) {
        queryClient.setQueryData<Address[]>(ADDRESSES_QUERY_KEY, (oldData) => {
          if (!oldData) return oldData;
          return oldData.map((addr) =>
            addr.addressId === message.addressId
              ? {
                  ...addr,
                  lastContactedAt: message.contact.contactedAt,
                  lastContactedBy: message.contact.contactedBy,
                  contactCount: (addr.contactCount || 0) + 1,
                }
              : addr
          );
        });
        console.log(
          `Updated address ${message.addressId} with new contact at ${message.contact.contactedAt}`
        );
      }

      // Handle tag added - add tagId to address.tags for filtering
      if (
        message.type === "tagAdded" &&
        message.addressId &&
        message.tag?.tagId
      ) {
        queryClient.setQueryData<Address[]>(ADDRESSES_QUERY_KEY, (oldData) => {
          if (!oldData) return oldData;
          return oldData.map((addr) => {
            if (addr.addressId !== message.addressId) return addr;
            const currentTags = addr.tags || [];
            if (currentTags.includes(message.tag.tagId)) return addr;
            return { ...addr, tags: [...currentTags, message.tag.tagId] };
          });
        });
      }

      // Handle tag removed - remove tagId from address.tags
      if (message.type === "tagRemoved" && message.addressId && message.tagId) {
        queryClient.setQueryData<Address[]>(ADDRESSES_QUERY_KEY, (oldData) => {
          if (!oldData) return oldData;
          return oldData.map((addr) => {
            if (addr.addressId !== message.addressId) return addr;
            return {
              ...addr,
              tags: (addr.tags || []).filter((t) => t !== message.tagId),
            };
          });
        });
      }
    },
  });

  /**
   * Update a specific address in the cache
   * @param addressId The address ID to update
   * @param updates Partial address object with fields to update
   */
  const updateAddress = useCallback(
    (addressId: string, updates: Partial<Address>) => {
      queryClient.setQueryData<Address[]>(ADDRESSES_QUERY_KEY, (oldData) => {
        if (!oldData) return oldData;
        const updated = oldData.map((addr) =>
          addr.addressId === addressId ? { ...addr, ...updates } : addr
        );
        return updated;
      });
      console.log(`Updated address ${addressId} in cache:`, updates);
    },
    [queryClient]
  );

  /**
   * Remove an address from the cache
   * @param addressId The address ID to remove
   */
  const removeAddress = useCallback(
    (addressId: string) => {
      queryClient.setQueryData<Address[]>(ADDRESSES_QUERY_KEY, (oldData) => {
        if (!oldData) return oldData;
        return oldData.filter((addr) => addr.addressId !== addressId);
      });
      console.log(`Removed address ${addressId} from cache`);
    },
    [queryClient]
  );

  /**
   * Force refresh all addresses from API (invalidates cache)
   */
  const refreshAddresses = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ADDRESSES_QUERY_KEY });
    await refetch();
  }, [queryClient, refetch]);

  /**
   * Refresh a specific address from API and update cache
   * @param addressId The address ID to refresh
   */
  const refreshAddress = useCallback(
    async (addressId: string) => {
      try {
        console.log(`Refreshing address ${addressId} from API...`);
        const allAddresses = await getAddresses();
        const address = allAddresses.find(
          (addr) => addr.addressId === addressId
        );

        if (address) {
          queryClient.setQueryData<Address[]>(
            ADDRESSES_QUERY_KEY,
            (oldData) => {
              if (!oldData) return oldData;
              return oldData.map((addr) =>
                addr.addressId === addressId ? { ...addr, ...address } : addr
              );
            }
          );
          console.log(`Refreshed address ${addressId} in cache`);
        } else {
          console.warn(`Address ${addressId} not found in API response`);
        }
      } catch (err: any) {
        console.error(`Error refreshing address ${addressId}:`, err);
        throw err;
      }
    },
    [queryClient]
  );

  /**
   * Load detailed address information (people, tags, notes) for a specific address
   * This updates the cache with the detailed data
   * @param addressId The address ID to load details for
   */
  const loadAddressDetail = useCallback(
    async (addressId: string): Promise<AddressDetail> => {
      try {
        console.log(`Loading detailed data for address ${addressId}...`);
        const detail = await getAddressDetail(addressId);

        // Update the address in cache with any new basic info
        queryClient.setQueryData<Address[]>(ADDRESSES_QUERY_KEY, (oldData) => {
          if (!oldData) return oldData;
          return oldData.map((addr) =>
            addr.addressId === addressId
              ? {
                  ...addr,
                  ...detail.address,
                  // Merge people details if they exist
                  peopleDetails: detail.people.map((person) => ({
                    stateId: person.stateId,
                    name: person.preferredName
                      ? `${person.preferredName} ${person.lastName}`.trim()
                      : `${person.firstName} ${person.lastName}`.trim(),
                    age: person.age || null,
                    sex: person.sex || null,
                    votingHistory: person.votingHistory || null,
                    links: person.links || undefined,
                    removedFromVoterRoll: person.removedFromVoterRoll || false,
                    movedToAddressId: person.movedToAddressId || null,
                    movedFromAddressId: person.movedFromAddressId || null,
                  })),
                }
              : addr
          );
        });

        return detail;
      } catch (err: any) {
        console.error(`Error loading address detail for ${addressId}:`, err);
        throw err;
      }
    },
    [queryClient]
  );

  return (
    <AddressContext.Provider
      value={{
        addresses,
        loading: isLoading,
        error,
        updateAddress,
        removeAddress,
        refreshAddresses,
        refreshAddress,
        loadAddressDetail,
      }}
    >
      {children}
    </AddressContext.Provider>
  );
}
